import { ReactElement, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import type { PanInfo } from 'motion-dom'
import { useUI } from '@corratech/pylot-ui/context'
import s from './BottomDrawer.module.scss'

const dropIn = {
    hidden: {
        y: '100vh'
    },
    visible: {
        y: '0',
        bottom: 0,
        transition: {
            type: 'spring',
            stiffness: 300,
            damping: 30
        }
    },
    exit: {
        y: 1000,
        transition: {
            y: {
                duration: 0.3
            }
        }
    }
}

const dropinProps = {
    initial: 'hidden',
    animate: 'visible',
    exit: 'exit'
}

export interface BottomDrawerProps {
    children: ReactElement
    onClose: () => void
}

export const BottomDrawer = ({
    children,
    onClose
}: BottomDrawerProps): ReactElement | null => {
    const { openBottomDrawer, closeBottomDrawer } = useUI()

    useEffect(() => {
        openBottomDrawer()
        return () => closeBottomDrawer()
        // Solo montaje/desmontaje: las acciones del UI context cambian de referencia con el state
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDragEnd = (event: any, info: PanInfo) => {
        if (info.offset.y > 100) {
            onClose()
        }
    }

    const drawer = (
        <motion.div
            onClick={() => onClose()}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000
            }}
        >
            <motion.div
                {...dropinProps}
                variants={dropIn}
                className={s['drawerContainer']}
                //eslint-disable-next-line
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.2 }}
                onDragEnd={handleDragEnd}
                onClick={(e) => e.stopPropagation()}
            >
                <motion.div className={s['handle']} />
                {children}
            </motion.div>
        </motion.div>
    )

    return typeof document !== 'undefined'
        ? createPortal(drawer, document.body)
        : null
}

export default BottomDrawer
